import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, NgZone, OnDestroy, TemplateRef, afterRenderEffect, computed, effect, inject, input, output, signal, untracked, viewChild, viewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SocialWallModel } from '../../models';
import { SocialWallApiService } from '../../services';
import { SocialWallAction } from '../social-wall-detail-dialog/social-wall-detail-dialog.component';
import { SocialWallDialogService } from '../social-wall-detail-dialog/social-wall-dialog.service';

export interface SocialWallCard {
  key: string;
  index: number;
  item: any;
}

const ESTIMATED_CARD_HEIGHT = 320;
const COLUMN_GAP = 24;
const MAX_CORRECTION_PASSES = 3;
const POSTS_PAGE_SIZE = 12;
const DEFAULT_SORT = 'pinned:DESC,created:DESC';

@Component({
  selector: 'lib-social-wall',
  templateUrl: './social-wall.component.html',
  styleUrl: './social-wall.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SocialWallComponent implements AfterViewInit, OnDestroy {

  public channel = input<string | null>(null);
  public defaultChannelId = input<string | null>(null);
  public author = input<string | null>(null);
  public maxColumns = input(3);
  public minColumnWidth = input(300);
  public contextTemplate = input<TemplateRef<any>>();
  public pageSize = input(POSTS_PAGE_SIZE);
  public enableLoadMore = input(true);

  public channelsLoaded = output<SocialWallModel[]>();
  public activeChannelId = output<string>();
  public channelAccessDenied = output<void>();
  public linkedContentClick = output<any>();

  public items = signal<any[]>([]);
  public activeChannel = signal<SocialWallModel | null>(null);
  public canComment = computed(() => this.activeChannel()?.allowUserComment !== false);
  public isLoading = signal(true);
  public isLoadingMorePosts = signal(false);
  public hasMorePosts = signal(false);
  public columnCount = signal(0);
  public isSettled = signal(false);
  public cards = computed<SocialWallCard[]>(() =>
    this.items().map((item, index) => ({ key: String(item?.id ?? index), index, item })));
  public columns = computed(() => this.distributeIntoColumns(this.cards(), this.columnCount(), this.measuredHeights()));
  private cardElements = viewChildren<ElementRef<HTMLElement>>('cardElement');
  private loadMoreSentinel = viewChild<ElementRef<HTMLElement>>('loadMoreSentinel');
  private measuredHeights = signal<ReadonlyMap<string, number>>(new Map());

  private readonly element = inject(ElementRef);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly socialWallDialogService = inject(SocialWallDialogService);
  private readonly socialWallApiService = inject(SocialWallApiService);

  private resizeObserver: ResizeObserver | null = null;
  private postsIntersectionObserver: IntersectionObserver | null = null;
  private scheduledFrame: number | null = null;
  private pendingContainerWidth: number | null = null;
  private correctionPasses = 0;
  private hasSettled = false;
  private latestRequestedChannelId: string | null = null;
  private postsOffset = 0;
  private hasResolvedInitialChannel = false;
  private channels: SocialWallModel[] = [];
  private postsRequestSubscription: Subscription | null = null;

  constructor() {
    afterRenderEffect({ read: () => {
      this.cardElements();
      this.measureCards();
    }});

    effect(() => {
      const channelId = this.channel();
      untracked(() => this.resolveChannel(channelId));
    });

    effect(() => {
      this.author();
      untracked(() => this.reloadCurrentChannel());
    });

    effect(() => {
      const sentinel = this.loadMoreSentinel()?.nativeElement;
      untracked(() => this.observeSentinel(sentinel));
    });
  }

  ngAfterViewInit(): void {
    const host = this.element.nativeElement as HTMLElement;

    this.ngZone.runOutsideAngular(() => {
      this.scheduleUpdate(host.clientWidth);

      this.resizeObserver = new ResizeObserver(entries => this.scheduleUpdate(entries[0]?.contentRect.width));
      this.resizeObserver.observe(host);

      host.addEventListener('load', this.onCardImageLoad, true);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.postsIntersectionObserver?.disconnect();
    this.element.nativeElement.removeEventListener('load', this.onCardImageLoad, true);

    if (this.scheduledFrame !== null) {
      cancelAnimationFrame(this.scheduledFrame);
    }
  }

  public openDetail(index: number): void {
    const dialog = this.socialWallDialogService.openDetail({ items: () => this.items(), index, canComment: this.canComment() });

    dialog.componentInstance.action
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(action => this.handleAction(action));
  }

  public onCardLike(item: any): void {
    this.handleLike({ type: 'like', item });
  }

  public onCardPin(item: any): void {
    this.handleAction({ type: 'pin', item });
  }

  public addPost(post: any): void {
    this.items.update(items => {
      const insertAt = items.findIndex(item => !item.pinned);
      const index = insertAt === -1 ? items.length : insertAt;

      return [...items.slice(0, index), post, ...items.slice(index)];
    });
  }

  private handleAction(action: SocialWallAction): void {
    if (action.type === 'like') {
      this.handleLike(action);
    } else if (action.type === 'pin') {
      this.handlePin(action);
    } else if (action.type === 'reply') {
      this.handleReply(action);
    } else if (action.type === 'loadComments') {
      this.handleLoadComments(action);
    } else if (action.type === 'openLinkedContent') {
      this.linkedContentClick.emit(action.content);
    }
  }

  private resolveChannel(requestedChannelId: string | null): void {
    if (this.hasResolvedInitialChannel) {
      if (requestedChannelId) {
        this.loadChannel(requestedChannelId);
      }

      return;
    }

    this.hasResolvedInitialChannel = true;

    this.socialWallApiService.getSocialWallChannels()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        const channels = response.items ?? [];
        this.channels = channels;
        this.channelsLoaded.emit(channels);

        const requestedChannel = requestedChannelId
          ? channels.find(channel => channel.id === requestedChannelId)
          : undefined;

        if (requestedChannelId && !requestedChannel) {
          this.channelAccessDenied.emit();
        }

        const defaultChannelId = this.defaultChannelId();
        const defaultChannel = channels.find(channel => channel.id === defaultChannelId) ?? channels[0];
        const resolvedChannel = requestedChannel ?? defaultChannel;

        if (resolvedChannel) {
          this.loadChannel(resolvedChannel.id);
        }
      });
  }

  private loadChannel(channelId: string): void {
    this.activeChannel.set(this.channels.find(channel => channel.id === channelId) ?? null);
    this.activeChannelId.emit(channelId);
    this.loadPosts(channelId);
  }

  private loadPosts(channelId: string): void {
    this.postsRequestSubscription?.unsubscribe();

    this.latestRequestedChannelId = channelId;
    this.postsOffset = 0;
    this.hasMorePosts.set(false);
    this.isLoading.set(true);
    this.items.set([]);

    const pageSize = this.pageSize();

    this.postsRequestSubscription = this.socialWallApiService.getSocialWallPosts(
      channelId,
      {
        limit: pageSize,
        offset: this.postsOffset,
        sort: DEFAULT_SORT,
        ...this.buildAuthorParam()
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe(response => {
        const loadedPosts = response.items ?? [];
        this.items.set(loadedPosts);
        this.hasMorePosts.set(this.enableLoadMore() && loadedPosts.length === pageSize);
      });
  }

  private loadMorePosts(): void {
    const channelId = this.latestRequestedChannelId;

    if (!channelId || this.isLoading() || this.isLoadingMorePosts() || !this.hasMorePosts()) {
      return;
    }

    const pageSize = this.pageSize();
    const nextOffset = this.postsOffset + pageSize;
    this.isLoadingMorePosts.set(true);

    this.postsRequestSubscription = this.socialWallApiService.getSocialWallPosts(channelId, { limit: pageSize, offset: nextOffset, sort: DEFAULT_SORT, ...this.buildAuthorParam() })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoadingMorePosts.set(false))
      )
      .subscribe(response => {
        const loadedPosts = response.items ?? [];
        this.postsOffset = nextOffset;
        this.items.update(items => [...items, ...loadedPosts]);
        this.hasMorePosts.set(loadedPosts.length === pageSize);
      });
  }

  private observeSentinel(sentinel: HTMLElement | undefined): void {
    this.postsIntersectionObserver?.disconnect();

    if (!sentinel) {
      return;
    }

    this.postsIntersectionObserver = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        this.loadMorePosts();
      }
    }, { rootMargin: '400px' });

    this.postsIntersectionObserver.observe(sentinel);
  }

  private handleLike(action: Extract<SocialWallAction, { type: 'like' }>): void {
    const likeTarget = action.reply ?? action.item;
    const wasLiked = !!likeTarget.userLiked;
    const isReplyLike = !!action.reply;
    const request$ = this.buildLikeRequest(likeTarget.id, isReplyLike, wasLiked);

    this.applyLikeToggle(action.item.id, action.reply?.id);

    request$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: () => this.applyLikeToggle(action.item.id, action.reply?.id) });
  }

  private buildLikeRequest(id: string, isReplyLike: boolean, wasLiked: boolean): Observable<void> {
    if (isReplyLike) {
      return wasLiked
        ? this.socialWallApiService.removeLikeSocialWallComment(id)
        : this.socialWallApiService.likeSocialWallComment(id);
    }

    return wasLiked
      ? this.socialWallApiService.removeLikeSocialWallPost(id)
      : this.socialWallApiService.likeSocialWallPost(id);
  }

  private handlePin(action: Extract<SocialWallAction, { type: 'pin' }>): void {
    const wasPinned = !!action.item.pinned;

    this.applyPinToggle(action.item.id);

    this.socialWallApiService.patchSocialWallPostPin(action.item.id, !wasPinned)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.reloadCurrentChannel(),
        error: () => this.applyPinToggle(action.item.id)
      });
  }

  private reloadCurrentChannel(): void {
    if (this.latestRequestedChannelId) {
      this.loadPosts(this.latestRequestedChannelId);
    }
  }

  private buildAuthorParam(): { author?: string } {
    const author = this.author();
    return author ? { author } : {};
  }

  private applyPinToggle(itemId: string): void {
    this.updateItem(itemId, item => ({ ...item, pinned: !item.pinned }));
  }

  private handleReply(action: Extract<SocialWallAction, { type: 'reply' }>): void {
    const parentCommentId = action.parentReplyId ?? '';

    this.socialWallApiService.createSocialWallComment(action.item.id, action.text, parentCommentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: newReply => this.applyNewReply(action.item.id, action.parentReplyId, newReply),
        // Forces the dialog's spinner-clearing effect to fire even though nothing changed.
        error: () => this.applyLoadedComments(action.item.id, action.parentReplyId ?? null, [], undefined, true)
      });
  }

  private handleLoadComments(action: Extract<SocialWallAction, { type: 'loadComments' }>): void {
    const offset = action.offset ?? 0;
    const parentCommentId = action.parentCommentId ?? null;
    const isLoadingMore = offset > 0;

    this.socialWallApiService.getSocialWallComments(action.item.id, undefined, offset, parentCommentId ?? '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        const loadedComments = response.items ?? [];
        const totalCommentsCount = response._meta?.maxcount;

        this.applyLoadedComments(action.item.id, parentCommentId, loadedComments, totalCommentsCount, isLoadingMore);
      });
  }

  private updateItem(itemId: string, updater: (item: any) => any): void {
    this.items.update(items => items.map(item => item.id === itemId ? updater(item) : item));
  }

  private updateNodeById(nodes: any[] | undefined, id: string, updater: (node: any) => any): any[] | undefined {
    if (!nodes) {
      return nodes;
    }

    let hasChanged = false;

    const updatedNodes = nodes.map(node => {
      const isMatch = node.id === id;

      if (isMatch) {
        hasChanged = true;
        return updater(node);
      }

      const updatedReplies = this.updateNodeById(node.replies, id, updater);
      const repliesChanged = updatedReplies !== node.replies;

      if (!repliesChanged) {
        return node;
      }

      hasChanged = true;
      return { ...node, replies: updatedReplies };
    });

    return hasChanged ? updatedNodes : nodes;
  }

  private nextCount(currentCount: number | undefined, existingList: any[] | undefined): number {
    const baseline = currentCount ?? existingList?.length ?? 0;
    return baseline + 1;
  }

  private applyLikeToggle(itemId: string, replyId?: string): void {
    this.updateItem(itemId, item => {
      if (!replyId) {
        return this.toggleLikeState(item);
      }

      const updatedReplies = this.updateNodeById(item.replies, replyId, reply => this.toggleLikeState(reply));
      return { ...item, replies: updatedReplies };
    });
  }

  private toggleLikeState(entity: any): any {
    const userLiked = !entity.userLiked;
    const likeCountDelta = userLiked ? 1 : -1;
    const totalLikes = (entity.totalLikes || 0) + likeCountDelta;

    return { ...entity, userLiked, totalLikes };
  }

  private applyNewReply(itemId: string, parentReplyId: string | null, reply: any): void {
    this.updateItem(itemId, item => {
      const commentsAmount = this.nextCount(item.commentsAmount, item.replies);
      const isTopLevelComment = !parentReplyId;

      if (isTopLevelComment) {
        const updatedReplies = [reply, ...(item.replies ?? [])];
        return { ...item, replies: updatedReplies, commentsAmount };
      }

      const updatedReplies = this.updateNodeById(item.replies, parentReplyId, existingReply => {
        const nestedReplies = [reply, ...(existingReply.replies ?? [])];
        const replaysAmount = this.nextCount(existingReply.replaysAmount, existingReply.replies);

        return { ...existingReply, replies: nestedReplies, replaysAmount };
      });

      return { ...item, replies: updatedReplies, commentsAmount };
    });
  }

  private applyLoadedComments(
    itemId: string, parentCommentId: string | null, comments: any[], total: number | undefined, append: boolean
  ): void {
    this.updateItem(itemId, item => {
      const isTopLevelComments = !parentCommentId;

      if (isTopLevelComments) {
        const updatedReplies = this.mergeReplies(item.replies, comments, append);
        // topCommentsTotal is only the top-level comment count, used to know when to stop
        // paginating - it's distinct from commentsAmount, which counts nested replies too.
        const topCommentsTotalUpdate = total != null ? { topCommentsTotal: total } : {};

        return { ...item, replies: updatedReplies, ...topCommentsTotalUpdate };
      }

      const updatedReplies = this.updateNodeById(item.replies, parentCommentId, existingReply => {
        const mergedReplies = this.mergeReplies(existingReply.replies, comments, append);
        const replaysAmountUpdate = total != null ? { replaysAmount: total } : {};

        return { ...existingReply, replies: mergedReplies, ...replaysAmountUpdate };
      });

      return { ...item, replies: updatedReplies };
    });
  }

  private mergeReplies(existingReplies: any[] | undefined, incomingReplies: any[], append: boolean): any[] {
    return append ? [...(existingReplies ?? []), ...incomingReplies] : incomingReplies;
  }

  private onCardImageLoad = (): void => this.scheduleUpdate();

  private scheduleUpdate(containerWidth?: number): void {
    this.pendingContainerWidth = containerWidth || this.pendingContainerWidth;

    if (this.scheduledFrame !== null) {
      return;
    }

    this.scheduledFrame = requestAnimationFrame(() => {
      this.scheduledFrame = null;

      if (this.pendingContainerWidth) {
        this.updateColumnCount(this.pendingContainerWidth);
        this.pendingContainerWidth = null;
      }

      this.measureCards();
    });
  }

  private updateColumnCount(containerWidth: number): void {
    const columnsThatFit = Math.floor((containerWidth + COLUMN_GAP) / (this.minColumnWidth() + COLUMN_GAP));
    const columnCount = Math.max(1, Math.min(this.maxColumns(), columnsThatFit));

    if (columnCount !== untracked(this.columnCount)) {
      this.columnCount.set(columnCount);
    }
  }

  private findShortestColumn(columnHeights: number[]): number {
    let shortest = 0;

    for (let column = 1; column < columnHeights.length; column++) {
      if (columnHeights[column] < columnHeights[shortest]) {
        shortest = column;
      }
    }

    return shortest;
  }

  private distributeIntoColumns(
    cards: SocialWallCard[],
    columnCount: number,
    measuredHeights: ReadonlyMap<string, number>,
    estimatedHeight = ESTIMATED_CARD_HEIGHT,
    gapBetweenCards = COLUMN_GAP): SocialWallCard[][] {
    const totalColumns = Math.max(1, columnCount);

    const columns: SocialWallCard[][] = Array.from({ length: totalColumns }, () => []);
    const columnHeights: number[] = new Array(totalColumns).fill(0);

    for (const card of cards) {
      const shortest = this.findShortestColumn(columnHeights);
      const cardHeight = measuredHeights.get(card.key) ?? estimatedHeight;
      const isFirstInColumn = !columns[shortest].length;

      columns[shortest].push(card);
      columnHeights[shortest] += cardHeight + (isFirstInColumn ? 0 : gapBetweenCards);
    }

    return columns;
  }

  private measureCards(): void {
    const cardElements = untracked(this.cardElements);

    if (!cardElements.length) {
      return;
    }

    const heights = new Map(untracked(this.measuredHeights));
    let hasChanged = false;

    for (const cardElement of cardElements) {
      const element = cardElement.nativeElement;
      const key = element.dataset['key'];
      const height = element.offsetHeight;

      if (!key || !height || heights.get(key) === height) {
        continue;
      }

      heights.set(key, height);
      hasChanged = true;
    }

    if (hasChanged) {
      this.measuredHeights.set(heights);
    }

    this.revealWhenSettled(hasChanged);
  }

  private revealWhenSettled(hasChanged: boolean): void {
    if (this.hasSettled) {
      return;
    }

    if (hasChanged && ++this.correctionPasses < MAX_CORRECTION_PASSES) {
      return;
    }

    this.hasSettled = true;
    this.isSettled.set(true);
  }
}
